import {
  MESSAGE_TYPES,
  MESSAGE_USER_TYPES_LIST,
} from '@sv-support-sdk/utils/src/constants/chatMessages';
import { getDevice, getUserAgentInfo } from '@sv-support-sdk/utils/src/helpers/deviceHelper';
import { toCamelCaseRecursive } from '@sv-support-sdk/utils/src/helpers/string';
import { ISupportAgent } from '@sv-support-sdk/chat-shared-types/src/common/chat';
import { IMessage } from '@sv-support-sdk/chat-shared-types/src/common/messages';

import socket from '../../socket';
import { TThunk } from '../index';
import { addMessage, setTyping, setUnreadMessages } from '../slices/messages';
import { setCookie } from '../../helpers/cookies';
import { CHAT_LAST_MESSAGE } from '../../constants/storage';
import { playSound } from '../../helpers/media';
import { TimeoutId } from '@reduxjs/toolkit/dist/query/core/buildMiddleware/types';
import { checkForBan, closeChat } from './chat';
import { CHAT_COMPANY } from '../../constants/chat';
import { addSupport } from '../slices/chat';
import { $l } from '../../locales';
import { readMessage } from './messages';

const device = getDevice();

const { browser } = getUserAgentInfo();
let clientPingConnection: TimeoutId;
let typingTimeout: TimeoutId;

const newMessageAction =
  (data: IMessage): TThunk =>
  (dispatch, getState) => {
    const {
      chat: { isOpen },
    } = getState();
    if (!MESSAGE_USER_TYPES_LIST.includes(data.authorType)) {
      if (isOpen && !window.document.hidden) {
        setCookie(CHAT_LAST_MESSAGE, data.id);
      } else {
        playSound();
        dispatch(setUnreadMessages(true));
      }
    }
    if (!MESSAGE_USER_TYPES_LIST.includes(data.authorType)) {
      dispatch(
        addSupport({
          id: data.authorableId,
          name: data.authorName,
          img: data.img,
        }),
      );
    }
    dispatch(addMessage({ ...data, type: data.type || MESSAGE_TYPES.MESSAGE }));
  };

export const newSystemMessage =
  (data: IMessage): TThunk =>
  (dispatch) => {
    playSound();

    dispatch(
      addMessage({
        ...data,
        type: MESSAGE_TYPES.SYSTEM,
        // @ts-expect-error custom key name
        message: $l(data.parameters.translation.key),
      }),
    );

    dispatch(readMessage());
  };

export const staffTypingAction =
  (data: ISupportAgent): TThunk =>
  (dispatch) => {
    clearTimeout(typingTimeout);

    dispatch(setTyping(data));

    typingTimeout = setTimeout(() => {
      dispatch(setTyping(null));
    }, 3000);
  };
export const initSocketConnectTo = (): TThunk => async (dispatch, getState) => {
  const {
    chat: { room },
  } = getState();
  clearInterval(clientPingConnection);
  const socketObj = socket();

  if (!socketObj) {
    console.error('Connection is unstable');
    return;
  }

  const userPing = () => {
    // @ts-expect-error with whisper
    socketObj.private(room).whisper('client-ping-connection', {
      browser: browser ? `${browser.name} ${browser.version}` : '',
      device,
    });
  };

  socketObj
    .private(room)
    .listen('Chat.NewChatMessage', (data: IMessage) =>
      dispatch(newMessageAction(toCamelCaseRecursive(data))),
    )
    .listen('Chat.NewSystemChatMessage', (data: IMessage) =>
      dispatch(newSystemMessage(toCamelCaseRecursive(data))),
    )
    .listen('Chat.ChatOpened', (data: IMessage) =>
      dispatch(newMessageAction(toCamelCaseRecursive(data))),
    )
    .listen('Chat.NewSystemChatMessage', (data: IMessage) =>
      dispatch(newMessageAction(toCamelCaseRecursive(data))),
    )
    .listen('Chat.ChatClosed', (data: { author_type: string }) => {
      const { chat: isOpen } = getState();
      if (
        !MESSAGE_USER_TYPES_LIST.includes(data.author_type) &&
        window.document.hidden &&
        !isOpen
      ) {
        playSound();
      }
      dispatch(closeChat());
    })
    .listen('Chat.Company.Updated', (data: { fromCompanyId: string; toCompanyId: string }) => {
      if (data.fromCompanyId && data.toCompanyId && data.fromCompanyId !== data.toCompanyId) {
        setCookie(CHAT_COMPANY, data.toCompanyId);
      }
    })
    .listenForWhisper('chatGotBan', () => dispatch(checkForBan()))
    .listenForWhisper('getUserInfoImmediately', userPing)
    .listenForWhisper('staffTyping', (data: ISupportAgent) =>
      dispatch(staffTypingAction(toCamelCaseRecursive(data))),
    );

  userPing();

  clientPingConnection = setInterval(userPing, 10000);
};
//
export const closeSocketRoomConnect = (): TThunk => (dispatch, getState) => {
  const {
    chat: { room },
  } = getState();
  clearInterval(clientPingConnection);
  const socketObj = socket();
  if (socketObj && room) {
    socketObj.leave(room);
  }
};
